import { Component, OnInit, ViewChild } from '@angular/core';
import { SecurityService } from '../../security/security.service';
import { CustomerProfile } from '../../security/models/customer-profile.model';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogWithCloseButtonComponent } from '../../utils/dialog-with-close-button/dialog-with-close-button.component';
import { HeaderComponent } from '../../shared/header/header.component';
import { Addr } from 'src/app/shared/model/addr.model';
import { DataService as SharedDataService } from 'src/app/shared/data.service';
import { DataService as CurriculumDataService } from 'src/app/middle-school-math/services/data';
import { SubscriptionPaywallDialogComponent } from 'src/app/shared/payment/subscription-paywall-dialog.component';

@Component({
  selector: 'app-customer-profile',
  templateUrl: './customer-profile.component.html',
  styleUrls: ['./customer-profile.component.scss'],
  standalone: false
})
export class CustomerProfileComponent implements OnInit {

  @ViewChild(HeaderComponent) header: HeaderComponent;

  buttonSelected: string = "MyAccount";
  loginId: string;
  custProfile = new CustomerProfile();
  countryPhoneCodes: any[] = [];

  userSubscriptions: any[] = [];
  isLoadingSubscriptions: boolean = false;
  booksMap: Map<string, any> = new Map<string, any>();

  popMsg = {
    'title': '',
    'message': ''
  };

  constructor(
    public dataSvc: SharedDataService,
    private authSvc: SecurityService,
    private curriculumDataSvc: CurriculumDataService,
    private router: Router,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // Check if user is ksolve-admin, redirect to admin dashboard
    const user = localStorage.getItem('User');
    if (user === 'ksolve-admin') {
      this.router.navigate(['/admin/dashboard']);
      return;
    }

    // 1. Fetch Phone Codes
    this.authSvc.getCountryPhoneCodes().subscribe({
      next: res => {
        this.countryPhoneCodes = res;
      },
      error: err => {
        console.log("Error fetching phone codes", err);
      }
    });

    // 2. Load Profile Data from DataService or Backend API
    const retrievedProfile = this.dataSvc.getCustomerProfile();
    if (retrievedProfile && retrievedProfile.loginId) {
      this.custProfile = retrievedProfile;
      this.ensureAddressInitialized();
      this.ensurePhoneDefaults();
    } else if (user) {
      this.authSvc.getCustomerProfileByLoginId(user).subscribe({
        next: (profile) => {
          if (profile) {
            this.custProfile = profile;
            this.dataSvc.setCustomerProfile(this.custProfile);
            this.ensureAddressInitialized();
            this.ensurePhoneDefaults();
          }
        },
        error: (err) => console.log("Error loading customer profile:", err)
      });
    }

    // 3. Load Curriculum Books & User Subscriptions
    this.loadCurriculumBooks();
    this.loadUserSubscriptions();

    console.log('Profile Initialized:', this.custProfile);
  }

  loadCurriculumBooks(): void {
    this.curriculumDataSvc.getCurriculum().subscribe({
      next: (manifest) => {
        if (manifest && manifest.books) {
          manifest.books.forEach(b => {
            this.booksMap.set(b.id, b);
            this.booksMap.set(b.id.replace(/-/g, '_'), b);
            this.booksMap.set(b.id.replace(/_/g, '-'), b);
          });
        }
      },
      error: (err) => console.warn('Could not load curriculum books map:', err)
    });
  }

  loadUserSubscriptions(): void {
    this.isLoadingSubscriptions = true;
    this.curriculumDataSvc.getMySubscriptions().subscribe({
      next: (subs) => {
        this.userSubscriptions = subs || [];
        this.isLoadingSubscriptions = false;
      },
      error: (err) => {
        console.warn('Could not load user subscriptions:', err);
        this.isLoadingSubscriptions = false;
      }
    });
  }

  isSubscriptionActive(sub: any): boolean {
    if (!sub) return false;
    const isStatusActive = sub.status === 'ACTIVE';
    const isPeriodValid = !sub.currentPeriodEnd || new Date(sub.currentPeriodEnd) > new Date();
    return isStatusActive && isPeriodValid;
  }

  getCourseTitle(sub: any): string {
    if (!sub?.bookId) return 'Course Subscription';
    const book = this.booksMap.get(sub.bookId);
    if (book?.title) return book.title;

    // Fallback beautification of slug
    if (sub.bookId === 'middle_school_math' || sub.bookId === 'middle-school-math') {
      return 'Middle School Math';
    }
    return sub.bookId.replace(/[_-]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  getCourseIcon(sub: any): string {
    const book = this.booksMap.get(sub?.bookId);
    return book?.icon || 'school';
  }

  openCourse(sub: any): void {
    if (!sub?.bookId) return;
    const targetBookId = sub.bookId;
    const route = (targetBookId === 'middle_school_math' || targetBookId === 'middle-school-math')
      ? '/middle-school-math'
      : `/${targetBookId}`;
    this.router.navigate([route]);
  }

  renewSubscription(sub: any): void {
    const book = this.booksMap.get(sub?.bookId) || {
      id: sub.bookId || 'middle_school_math',
      title: this.getCourseTitle(sub),
      isSubscriptionEnabled: true,
      monthlyPrice: 9.99,
      annualPrice: 79.99,
      currency: sub.currency || 'USD'
    };

    const dialogRef = this.dialog.open(SubscriptionPaywallDialogComponent, {
      width: '650px',
      maxWidth: '95vw',
      panelClass: 'paywall-modal-container',
      data: {
        book: book,
        suggestedPlan: sub.planType || 'ANNUAL'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.purchased) {
        this.loadUserSubscriptions();
      }
    });
  }

  private ensureAddressInitialized() {
    if (!this.custProfile.homeOrOfficeAddress) {
      this.custProfile.homeOrOfficeAddress = new Addr();
    }
    // Map flat billing fields from backend to nested homeOrOfficeAddress object
    if (this.custProfile.billingAddressLine1) {
      this.custProfile.homeOrOfficeAddress.addressLine1 = this.custProfile.billingAddressLine1;
    }
    if (this.custProfile.billingAddressLine2) {
      this.custProfile.homeOrOfficeAddress.addressLine2 = this.custProfile.billingAddressLine2;
    }
    if (this.custProfile.billingCity) {
      this.custProfile.homeOrOfficeAddress.city = this.custProfile.billingCity;
    }
    if (this.custProfile.billingStateOrProvinceId) {
      this.custProfile.homeOrOfficeAddress.stateOrProvinceId = this.custProfile.billingStateOrProvinceId;
    }
    if (this.custProfile.billingCountryId) {
      this.custProfile.homeOrOfficeAddress.countryId = this.custProfile.billingCountryId;
    }
    if (this.custProfile.billingZipOrPostalCode) {
      this.custProfile.homeOrOfficeAddress.zipOrPostalCode = this.custProfile.billingZipOrPostalCode;
    }
    if (!this.custProfile.homeOrOfficeAddress.countryId) {
      this.custProfile.homeOrOfficeAddress.countryId = 231; // Default US
    }

    if (!this.custProfile.mailingOrShippingAddress) {
      this.custProfile.mailingOrShippingAddress = new Addr();
    }
    // Map flat shipping fields from backend to nested mailingOrShippingAddress object
    if (this.custProfile.shippingAddressLine1) {
      this.custProfile.mailingOrShippingAddress.addressLine1 = this.custProfile.shippingAddressLine1;
    }
    if (this.custProfile.shippingAddressLine2) {
      this.custProfile.mailingOrShippingAddress.addressLine2 = this.custProfile.shippingAddressLine2;
    }
    if (this.custProfile.shippingCity) {
      this.custProfile.mailingOrShippingAddress.city = this.custProfile.shippingCity;
    }
    if (this.custProfile.shippingStateOrProvinceId) {
      this.custProfile.mailingOrShippingAddress.stateOrProvinceId = this.custProfile.shippingStateOrProvinceId;
    }
    if (this.custProfile.shippingCountryId) {
      this.custProfile.mailingOrShippingAddress.countryId = this.custProfile.shippingCountryId;
    }
    if (this.custProfile.shippingZipOrPostalCode) {
      this.custProfile.mailingOrShippingAddress.zipOrPostalCode = this.custProfile.shippingZipOrPostalCode;
    }
    if (!this.custProfile.mailingOrShippingAddress.countryId) {
      this.custProfile.mailingOrShippingAddress.countryId = 231;
    }
  }

  private ensurePhoneDefaults() {
    if (!this.custProfile.primaryPhoneCountryCode) {
      this.custProfile.primaryPhoneCountryCode = 1; // Default +1
    }
    if (!this.custProfile.altPhoneCountryCode) {
      this.custProfile.altPhoneCountryCode = 1;
    }
  }

  cancel() {
    console.log(this.custProfile);
  }

  isPhoneFormComplete(): boolean {
    if (!this.custProfile.primaryPhone || this.custProfile.primaryPhone.trim() == "") {
      this.popMsg.title = 'Incomplete Form';
      this.popMsg.message = 'Please enter the primary phone number';
      this.openDialog(this.popMsg);
      return false;
    }

    if (this.custProfile.primaryPhone.length < 10) {
      this.popMsg.title = 'Invalid phone number';
      this.popMsg.message = 'Phone number should be at least 10 digits.';
      this.openDialog(this.popMsg);
      return false;
    }
    return true;
  }

  openDialog(popupMessage: any) {
    this.dialog.open(DialogWithCloseButtonComponent, { data: popupMessage });
  }

  homeOrOfficeAddressChangedEvent(address: Addr) {
    console.log("Home Address Changed:", address);
    this.custProfile.homeOrOfficeAddress = address;

    this.authSvc.saveUserHomeOrOfficeAddress(this.custProfile).subscribe({
      next: res => {
        this.popMsg.title = "Success";
        this.popMsg.message = "Home/Office Address updated successfully.";
        this.openDialog(this.popMsg);
      },
      error: err => {
        this.popMsg.title = "Save failed";
        this.popMsg.message = "Could not save address. Please try again.";
        console.error(err);
        this.openDialog(this.popMsg);
      }
    });
  }

  mailingAddressChangedEvent(address: Addr) {
    console.log("Mailing Address Changed:", address);
    this.custProfile.mailingOrShippingAddress = address;
    this.authSvc.saveUserMailingOrShippingAddress(this.custProfile).subscribe({
      next: res => {
        this.popMsg.title = "Success";
        this.popMsg.message = "Shipping Address updated successfully.";
        this.openDialog(this.popMsg);
      },
      error: err => {
        this.popMsg.title = "Save failed";
        this.popMsg.message = "Could not save address. Please try again.";
        console.error(err);
        this.openDialog(this.popMsg);
      }
    });
  }

  copyHomeOrOfficeAddress() {
    const source = this.custProfile.homeOrOfficeAddress;
    if (source) {
      this.custProfile.mailingOrShippingAddress = { ...source };
    }
  }

  changePassword() {
    let data = { 'pageTitle': 'Change Password' };
    this.dataSvc.setSharedData(data);
    this.router.navigate(['/forgot-password']);
  }

  saveCustomerPhones() {
    if (!this.isPhoneFormComplete()) return;

    this.authSvc.saveUserPhones(this.custProfile).subscribe({
      next: res => {
        this.popMsg.title = "Success";
        this.popMsg.message = "Contact Information updated successfully.";
        this.openDialog(this.popMsg);
      },
      error: err => {
        this.popMsg.title = "Save failed";
        this.popMsg.message = "Could not save phones. Please try again.";
        console.error(err);
        this.openDialog(this.popMsg);
      }
    });
  }

}
